import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { Checkbox } from '@delmonicos/shared-design-system-react/src/components/checkbox/checkbox';
import { TextInput } from '@delmonicos/shared-design-system-react/src/components/input/text-input';
import { withProtectedResource } from '@delmonicos/shared-design-system-react/src/components/protected-resources/with-protected-resource.helper';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { WithStatePaginatedTable } from '@delmonicos/shared-design-system-react/src/components/with-state-paginated-table/with-state-paginated-table';
import { HeadingEnum } from '@delmonicos/shared-design-system-react/src/models/enums/heading.enum';
import { GetCustomersPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/customers/models/get-customers-paginated.model';
import { GatewayCustomerModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/customers/models/outputs/gateway-customer.model';
import { useGatewayCreateUserFormValidation } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/users/models/inputs/gateway-create-user.form-validation';
import { GatewayCreateUserModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/users/models/inputs/gateway-create-user.model';
import { safeHasAccessTo } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { transformPaginatedResponse } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/transform-api-paginated-response/transform-api-response';
import { TransformedPaginatedData } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/transform-api-paginated-response/transformed-paginated-data.model';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import {
  CellContext,
  OnChangeFn,
  SortingState,
  createColumnHelper,
  functionalUpdate,
} from '@tanstack/react-table';
import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { permissions } from '@app/core/authentication/authentication.slice';
import { getState } from '@app/core/helper/get-state';
import { useAppSelector } from '@app/core/hooks';
import { StateEnum } from '@app/core/model/enum/state.enum';
import { CustomerListHolderCompanyNameCell } from '@app/feature/customer/customer-list/customer-list-holder-company-name.cell';
import { CustomerListIdCell } from '@app/feature/customer/customer-list/customer-list-id.cell';
import { CustomerListNameCell } from '@app/feature/customer/customer-list/customer-list-name.cell';
import { CUSTOMER_LIST_SORT_FIELD } from '@app/feature/customer/customer-list/customer-list.config';
import {
  CustomerListContextProvider,
  useCustomerListContext,
} from '@app/feature/customer/customer.context';
import { useCpmsBoGetCustomersPaginatedQuery } from '@service/cpms-bo-customer.service';
import * as globalCss from '@shared/styles/global.module.scss';

export interface UserCreateFormCustomerSelectProps
  extends CellContext<GatewayCustomerModel, unknown> {}

export const UserCreateFormCustomerSelect = (
  props: UserCreateFormCustomerSelectProps,
): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { row } = props;
  const { control } = useFormContext<GatewayCreateUserModel>();
  const { customerId } = useGatewayCreateUserFormValidation();

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Controller
      name="customerId"
      control={control}
      rules={customerId}
      render={({ field }) => {
        const checked = field.value
          ? field.value.includes(row.original.id)
          : false;

        const value = field.value
          ? Array.isArray(field.value)
            ? field.value
            : [field.value]
          : [];

        return (
          <Checkbox
            id={row.id}
            label=""
            checked={checked}
            {...field}
            onChange={() => {
              if (checked) {
                field.onChange(value.filter((id) => id !== row.original.id));
              } else {
                field.onChange([...value, row.original.id]);
              }
            }}
          />
        );
      }}
    />
  );
};

// /////////////////////////////////////////////////////////////////////////////

const columnHelper = createColumnHelper<GatewayCustomerModel>();

const STEP1_CUSTOMER_COLUMN_ID_LIST = [
  'select',
  'id',
  'name',
  'holder.companyName',
];
const STEP1_CUSTOMER_COLUMN_ORDER = [...STEP1_CUSTOMER_COLUMN_ID_LIST];
const STEP1_CUSTOMER_COLUMN_VISIBILITY = STEP1_CUSTOMER_COLUMN_ID_LIST.reduce(
  (acc, id) => ({ ...acc, [id]: true }),
  {},
);

interface UserCreateFormStep1CustomerViewProps {
  paginationLimit: number;
  paginationLimitList: number[];
  paginationState: StateEnum;
  paginationSorting: SortingState;
  resource?: TransformedPaginatedData<GatewayCustomerModel>;
  onPaginationLimitChange: (paginationLimitNext: number) => void;
  onPaginationPageChange: (paginationPageNext: number) => void;
  onPaginationSortingChange?: OnChangeFn<SortingState>;
}

export const UserCreateFormStep1CustomerView = ({
  paginationLimit,
  paginationLimitList,
  paginationState,
  paginationSorting,
  resource,
  onPaginationLimitChange,
  onPaginationPageChange,
  onPaginationSortingChange,
}: UserCreateFormStep1CustomerViewProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  // LIST //////////////////////////////////////////////////////////////////////

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: '',
        cell: UserCreateFormCustomerSelect,
      }),
      columnHelper.accessor('id', {
        enableSorting: false,
        header: t('delmonicos-shared-sdk.outputs-base.id'),
        cell: CustomerListIdCell,
      }),
      columnHelper.accessor('name', {
        enableSorting: false,
        header: t('delmonicos-shared-sdk.outputs-customer.name'),
        cell: CustomerListNameCell,
      }),
      columnHelper.accessor('holder.companyName', {
        enableSorting: false,
        header: t('delmonicos-shared-sdk.outputs-holder.company-name'),
        cell: CustomerListHolderCompanyNameCell,
      }),
    ],
    [t],
  );

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <WithStatePaginatedTable
      paginatedData={resource}
      state={paginationState}
      sorting={paginationSorting}
      columns={columns}
      columnOrder={STEP1_CUSTOMER_COLUMN_ORDER}
      columnVisibility={STEP1_CUSTOMER_COLUMN_VISIBILITY}
      limit={paginationLimit}
      limitList={paginationLimitList}
      errorMessage={t('$.customer-list.error')}
      onLimitChange={onPaginationLimitChange}
      onPageChange={onPaginationPageChange}
      onSortingChange={onPaginationSortingChange}
    />
  );
};

const UserCreateFormStep1CustomerController = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const {
    getPaginatedQueryParameters,
    setGetPaginatedQueryParameters,
    getPaginatedLimitList,
  } = useCustomerListContext();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  // ACTIONS ///////////////////////////////////////////////////////////////////

  // Nothing to do

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const queryState = useCpmsBoGetCustomersPaginatedQuery(
    getPaginatedQueryParameters,
  );

  const paginationState = useMemo(
    () =>
      getState({
        isLoading: queryState.isLoading,
        isError: queryState.isError,
        isFetching: queryState.isFetching,
      }),
    [queryState.isLoading, queryState.isError, queryState.isFetching],
  );

  // PAGE TITLE ////////////////////////////////////////////////////////////////

  // Nothing to do

  // PAGE ACTIONS //////////////////////////////////////////////////////////////

  // Nothing to do

  // PAGE BREADCRUMB ///////////////////////////////////////////////////////////

  // Nothing to do

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handlePaginationPageChange = (paginationPageNext: number) => {
    setGetPaginatedQueryParameters((current) => ({
      ...current,
      offset: (paginationPageNext - 1) * current.limit,
    }));
  };

  const handlePaginationLimitChange = (paginationLimitNext: number) => {
    setGetPaginatedQueryParameters((current) => ({
      ...current,
      limit: paginationLimitNext,
      offset: 0,
    }));
  };

  const handlePaginationSortingChange: OnChangeFn<SortingState> = (
    sortingState,
  ) => {
    setGetPaginatedQueryParameters((current) => {
      const paginationSorting0 = functionalUpdate(sortingState, [
        {
          desc: current.sortOrder === -1,
          id: current.sortField ?? CUSTOMER_LIST_SORT_FIELD,
        },
      ]).at(0);

      return {
        ...current,
        sortField:
          paginationSorting0?.id as GetCustomersPaginatedModel['sortField'],
        sortOrder: paginationSorting0?.desc ? -1 : 1,
      };
    });
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const { Component: UserCreateFormStep1CustomerViewProtected } =
    withProtectedResource(UserCreateFormStep1CustomerView, queryState, {
      isAuthorized: () =>
        safeHasAccessTo(userPermissions, {
          resourceName: Resource.CUSTOMERS,
          action: ActionsByResource.CUSTOMERS.READ,
        }),
      transformDataToResource: transformPaginatedResponse,
    })({
      paginationLimit: getPaginatedQueryParameters.limit,
      paginationLimitList: getPaginatedLimitList,
      paginationState,
      paginationSorting: [
        {
          desc: getPaginatedQueryParameters.sortOrder === -1,
          id: getPaginatedQueryParameters.sortField ?? CUSTOMER_LIST_SORT_FIELD,
        },
      ],
      onPaginationLimitChange: handlePaginationLimitChange,
      onPaginationPageChange: handlePaginationPageChange,
      onPaginationSortingChange: handlePaginationSortingChange,
    });

  return (
    <Card flex={1} direction="column" gap={GapEnum.M}>
      <AccentTitle
        as={HeadingEnum.H2}
        icon="dataflow-04"
        text={t('$.user-create.step-1.customer.title')}
        variant={AccentVariantEnum.Accent1}
      />
      {UserCreateFormStep1CustomerViewProtected}
    </Card>
  );
};

const UserCreateFormStep1UserController = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { control } = useFormContext<GatewayCreateUserModel>();
  const { lastName, firstName, emailAddress, mobileNumber, landlineNumber } =
    useGatewayCreateUserFormValidation();

  return (
    <Card direction="column" flex={1} gap={GapEnum.M}>
      <AccentTitle
        as={HeadingEnum.H2}
        icon="user-01"
        text={t('$.user-create.step-1.user.title')}
      />
      <Controller
        name="emailAddress"
        control={control}
        rules={emailAddress}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t('delmonicos-shared-sdk.outputs-user.email-address')}
            placeholder={t('delmonicos-shared-sdk.outputs-user.email-address')}
            required={!!emailAddress.required}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(e) =>
              field.onChange(e.target.value === '' ? '' : e.target.value)
            }
          />
        )}
      />
      <Stack gap={GapEnum.M} flexWrap="wrap" className={globalCss.flexedItems}>
        <Controller
          name="firstName"
          control={control}
          rules={firstName}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-user.first-name')}
              placeholder={t('delmonicos-shared-sdk.outputs-user.first-name')}
              required={!!firstName.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? '' : e.target.value)
              }
            />
          )}
        />
        <Controller
          name="lastName"
          control={control}
          rules={lastName}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-user.last-name')}
              placeholder={t('delmonicos-shared-sdk.outputs-user.last-name')}
              required={!!lastName.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? '' : e.target.value)
              }
            />
          )}
        />
      </Stack>
      <Stack gap={GapEnum.M} flexWrap="wrap" className={globalCss.flexedItems}>
        <Controller
          name="mobileNumber"
          control={control}
          rules={mobileNumber}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-user.mobile-number')}
              placeholder={t(
                'delmonicos-shared-sdk.outputs-user.mobile-number',
              )}
              required={!!mobileNumber.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? '' : e.target.value)
              }
            />
          )}
        />
        <Controller
          name="landlineNumber"
          control={control}
          rules={landlineNumber}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-user.landline-number')}
              placeholder={t(
                'delmonicos-shared-sdk.outputs-user.landline-number',
              )}
              required={!!landlineNumber.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? '' : e.target.value)
              }
            />
          )}
        />
      </Stack>
    </Card>
  );
};

export const UserCreateFormStep1Controller = (): React.JSX.Element => {
  return (
    <Stack gap={GapEnum.L} flex={1}>
      <UserCreateFormStep1UserController />
      <CustomerListContextProvider>
        <UserCreateFormStep1CustomerController />
      </CustomerListContextProvider>
    </Stack>
  );
};
